import { months } from "../../constants";

/**
 *
 * @example formatAmount(100099)
 * @returns R$ 1.000,99
 */
export const formatAmount = (value: number): string => {
	if (!value) {
		return "R$ 0,00";
	}

	return new Intl.NumberFormat("pt-BR", {
		style: "currency",
		currency: "BRL",
		maximumFractionDigits: 2,
		minimumFractionDigits: 2,
	}).format(value);
};
/**
 *
 * @example formatAmount(100099)
 * @returns R$ 1.000
 * rounding floor
 */
export const formatAmountWithNoCents = (value: number): string => {
	if (!value) {
		return "R$ 0";
	}

	return new Intl.NumberFormat("pt-BR", {
		style: "currency",
		currency: "BRL",
		maximumFractionDigits: 0,
		minimumFractionDigits: 0,
	}).format(Math.floor(value));
};

/**
 *
 * @exampe onlyNumber("R$ 1.000,99")
 * @returns 100099
 */
export const onlyNumber = (value: string): string => value.replace(/\D/g, "");

/**
 * @example parseMoneyToDecimal("R$ 1.000,99")
 * @returns 1000.99
 */
export const parseMoneyToDecimal = (value: string): number => {
	if (!value) {
		return 0;
	}

	const numberInString = onlyNumber(value);
	const numberLength = numberInString.length;

	if (numberLength <= 1) {
		return parseFloat("0" + "." + "0" + numberInString);
	}

	const reais = numberInString.slice(0, -2);
	const centavos = numberInString.slice(numberLength - 2, numberLength);

	const number = parseFloat(reais + "." + centavos);

	return number;
};

export const formatMonthByNumber = (value: number): string => {
	const index = months.findIndex((month) => month.value === value.toString());

	if (index > -1) {
		return months[index].text;
	}

	return months[0].text;
};

export const formatCellphoneNumber = (value: string): string =>
	value.replace(/(\d{2})(\d{1})(\d{4})(\d{4})/, "($1) $2$3 $4");

export const formatSeconds = (secondsRemaining: number): string => {
	const hour = Math.floor(secondsRemaining / 3600);
	const minutes = Math.floor((secondsRemaining % 3600) / 60);
	const seconds = secondsRemaining % 60;

	return `${hour > 0 ? hour + ":" : ""}${minutes}:${seconds < 10 ? "0" + seconds : seconds}`;
};

export const formatDocumentNumber = (value: string): string => {
	const numericValue = value.replace(/\D/g, "");
	const formattedValue = numericValue.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");

	return formattedValue;
};

export const formatCEP = (cep: string): string =>
	cep
		.replace(/\D/g, "")
		.replace(/(\d{5})(\d)/, "$1-$2")
		.replace(/(-\d{3})\d+?$/, "$1");

export const formatDateDDMMYYYY = (value: string): string => {
	const numericValue = onlyNumber(value);
	const formattedValue = numericValue.replace(/^([0-9]{1,2})([0-9]{1,2})([0-9]{4})$/, "$1/$2/$3");

	return formattedValue;
};

/**
 * @example formatPercentage(0.2720)
 * @returns "27.20%"
 */
export const formatPercentage = (value: number): string =>
	Intl.NumberFormat("en-US", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(value * 100) + "%";

export const invertDate = (dateToFormat: string): string => dateToFormat.split("-").reverse().join("-");

export const switchSlashesToHyphens = (value: string): string => value.replace(/\//g, "-");

export const switchHyphensToSlashes = (value: string): string => value.replace(/-/g, "/");

/**
 * @example cpfPrivacyMask(12345678900) or cpfPrivacyMask(123.456.789-00)
 * @returns "***.456.789-**"
 */
export const cpfPrivacyMask = (value: string): string => {
	value = value.replace(/\D/g, "");

	return value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "***.$2.$3-**");
};

/**
 * Devolve apenas o primeiro nome e com a primeira letra maíuscula
 * @example onlyFirstName("patricio seja fuerte")
 * @returns "Patricio"
 */
export const onlyFirstName = (value: string): string => {
	if (value === "") {
		return "";
	}

	const firstName = value.split(" ")[0];
	return firstName[0].toUpperCase() + firstName.substring(1);
};

export const formatCallCenterPhone = (value: string): string => {
	const cleanNumber = value.replace(/\D/g, "");
	const countryCode = cleanNumber.slice(0, 4);
	const areaCode = cleanNumber.slice(4, 6);
	const part1 = cleanNumber.slice(6, 9);
	const part2 = cleanNumber.slice(9);

	return `${countryCode} ${areaCode} ${part1} ${part2}`;
};

export const formatDate = (value: string): string => {
	const date = new Date(value);
	const formattedDate = new Intl.DateTimeFormat("pt-BR").format(date);

	return formattedDate;
};

export const cleanString = (text: string): string => {
	// Remove o ponto final do fim da texting
	text = text.replace(/\.$/, "");
	// Substitui espaços duplos (ou múltiplos) por um único espaço
	text = text.replace(/\s+/g, " ");
	return text;
};

// formata data para fuso de brasília e converte para milissegundos
export const formatDateTimeZoneToMillisecondsTimeZone = (date: Date): number | string => {
	// Obter o deslocamento de fuso horário em minutos do sistema local
	const timezoneOffset = date.getTimezoneOffset();

	// Calcular o horário local adicionando o deslocamento (em milissegundos)
	const localTime = new Date(date.getTime() - timezoneOffset * 60000);

	// Extrair e formatar manualmente a data e hora
	const year = localTime.getFullYear();
	const month = String(localTime.getMonth() + 1).padStart(2, "0");
	const day = String(localTime.getDate()).padStart(2, "0");
	const hours = String(localTime.getHours()).padStart(2, "0");
	const minutes = String(localTime.getMinutes()).padStart(2, "0");
	const seconds = String(localTime.getSeconds()).padStart(2, "0");

	// formato padrão (YYYY-MM-DD HH:MM:SS)
	const dateFormatted = new Date(`${year}-${month}-${day} ${hours}:${minutes}:${seconds}`);

	return dateFormatted.getTime();
};

// formata redirectURL para login único
export const formatRedirectURLLoginUnico = (): string => {
	const origin = window.location.origin;

	if (origin.includes("localhost")) {
		return origin;
	}

	const redirectURL = origin.replace("http://", "https://").replace("www.", "");

	return redirectURL;
};
